import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Lightbulb, Volume2, Loader2 } from "lucide-react";

interface StepCardProps {
  stepNumber: number;
  title: string;
  explanation: string;
  work?: string;
  isActive?: boolean;
  isVoiceHighlighted?: boolean;
  onExplainDeeper?: () => Promise<string> | string | void;
}

export function StepCard({
  stepNumber,
  title,
  explanation,
  work,
  isActive = false,
  isVoiceHighlighted = false,
  onExplainDeeper,
}: StepCardProps) {
  const [expanded, setExpanded] = useState(isActive);
  const [deeper, setDeeper] = useState<string | null>(null);
  const [deeperLoading, setDeeperLoading] = useState(false);
  const [deeperError, setDeeperError] = useState<string | null>(null);

  const handleExplainDeeper = async () => {
    if (!onExplainDeeper || deeperLoading) return;
    if (deeper) {
      // toggle off
      setDeeper(null);
      return;
    }
    setDeeperLoading(true);
    setDeeperError(null);
    try {
      const result = onExplainDeeper();
      const text = typeof result === "string" ? result : await result;
      if (text) setDeeper(text);
    } catch (e) {
      setDeeperError(e instanceof Error ? e.message : "Could not fetch deeper explanation");
    } finally {
      setDeeperLoading(false);
    }
  };

  // Auto-expand when voice is highlighting this step
  useEffect(() => {
    if (isVoiceHighlighted && !expanded) {
      setExpanded(true);
    }
  }, [isVoiceHighlighted]);

  return (
    <div
      className={`relative z-10 flex gap-4 md:gap-6 items-start group transition-all duration-300 ${
        isVoiceHighlighted ? "scale-[1.01]" : ""
      }`}
    >
      {/* Step number circle */}
      <div
        className={`size-7 rounded-full shrink-0 flex items-center justify-center font-[family-name:var(--font-mono)] font-bold text-xs mt-1 shadow-sm transition-colors ${
          isVoiceHighlighted
            ? "bg-neon-blue text-paper border-2 border-neon-blue animate-pulse"
            : expanded
              ? "bg-neon-pink text-paper border-2 border-neon-pink"
              : "bg-card border-2 border-rule text-ink-muted group-hover:border-neon-yellow group-hover:text-ink"
        }`}
      >
        {isVoiceHighlighted ? (
          <Volume2 className="size-3.5" />
        ) : (
          stepNumber
        )}
      </div>

      {/* Step content */}
      {expanded ? (
        <div
          className={`flex-1 rounded-2xl p-5 md:p-6 border shadow-[0_4px_20px_-4px_rgba(42,40,36,0.06)] relative overflow-hidden transition-all duration-300 ${
            isVoiceHighlighted
              ? "bg-card border-neon-blue/40 ring-2 ring-neon-blue/20"
              : "bg-card border-rule"
          }`}
        >
          <div
            className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${
              isVoiceHighlighted ? "bg-neon-blue" : "bg-neon-pink"
            }`}
          />

          <button
            onClick={() => setExpanded(false)}
            className="flex items-center justify-between w-full mb-3"
          >
            <h3 className="font-[family-name:var(--font-display)] font-bold text-base text-ink">
              {title}
            </h3>
            <ChevronDown className="size-4 text-ink-muted" />
          </button>

          <p className="text-sm text-ink-muted leading-relaxed mb-4">
            {explanation}
          </p>

          {work && (
            <div className="bg-brand-light/50 rounded-xl p-4 font-[family-name:var(--font-mono)] text-sm text-ink whitespace-pre-line border border-rule/40">
              {work}
            </div>
          )}

          {onExplainDeeper && (
            <div className="mt-4 space-y-3">
              <button
                onClick={handleExplainDeeper}
                disabled={deeperLoading}
                className="flex items-center gap-2 text-xs font-semibold text-neon-pink hover:text-neon-pink/80 transition-colors disabled:opacity-60"
              >
                {deeperLoading ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" /> Thinking...
                  </>
                ) : deeper ? (
                  <>
                    <Lightbulb className="size-3.5" /> Hide deeper explanation
                  </>
                ) : (
                  <>
                    <Lightbulb className="size-3.5" /> Explain Deeper
                  </>
                )}
              </button>
              {deeper && (
                <div className="bg-neon-blue/5 border border-neon-blue/20 rounded-xl p-4 text-sm text-ink leading-relaxed whitespace-pre-line">
                  {deeper}
                </div>
              )}
              {deeperError && (
                <div className="text-xs text-destructive">{deeperError}</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setExpanded(true)}
          className="flex-1 bg-paper rounded-2xl p-4 md:p-5 border border-rule hover:bg-card cursor-pointer transition-colors flex items-center justify-between"
        >
          <h3 className="font-[family-name:var(--font-display)] font-medium text-sm text-ink">
            {title}
          </h3>
          <ChevronRight className="size-4 text-ink-muted" />
        </button>
      )}
    </div>
  );
}
